import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { format, startOfMonth, endOfMonth, isWithinInterval, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PageLayout from '../PageLayout';

const ASSET_TYPES = [
  { id: 'acao', label: 'Ação' },
  { id: 'fii', label: 'FII' },
  { id: 'rendaFixa', label: 'Renda Fixa' },
  { id: 'cripto', label: 'Criptomoedas' },
  { id: 'etf', label: 'ETF' },
  { id: 'cdb', label: 'CDB' },
  { id: 'tesouro_direto', label: 'Tesouro Direto' },
  { id: 'lci', label: 'LCI' },
  { id: 'lca', label: 'LCA' },
  { id: 'fundos', label: 'Fundos de Investimento' },
  { id: 'poupanca', label: 'Poupança' },
  { id: 'conta_remunerada', label: 'Conta remunerada' },
  { id: 'outro', label: 'Outro' },
];

const INCOME_TYPES = [
  { id: 'dividendos', label: 'Dividendos' },
  { id: 'jcp', label: 'JCP' },
  { id: 'juros', label: 'Juros' },
  { id: 'rendimentos', label: 'Rendimentos' },
  { id: 'outros', label: 'Outros' },
];

const PIE_COLORS = {
  acao: '#1565C0',
  fii: '#2E7D32',
  rendaFixa: '#F9A825',
  cripto: '#7B1FA2',
  etf: '#E65100',
};

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(Number(v) || 0);
}

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

const DEFAULT_INVESTMENTS = {
  assets: [],
  contributions: [],
  income: [],
  contributionGoal: 0,
  upcomingDividends: [],
};

export default function ProsperityInvestments({ customColors }) {
  const { currentUser } = useAuth();
  const [data, setData] = useState(DEFAULT_INVESTMENTS);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [assetForm, setAssetForm] = useState({
    name: '',
    type: 'acao',
    investedValue: '',
    currentValue: '',
    date: format(new Date(), 'yyyy-MM-dd'),
  });
  const [editingAssetId, setEditingAssetId] = useState(null);

  const [contributionDialogOpen, setContributionDialogOpen] = useState(false);
  const [editingContributionId, setEditingContributionId] = useState(null);
  const [contributionForm, setContributionForm] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    investmentName: '',
    assetId: '',
    assetType: '',
    assetRisk: 'renda_fixa',
    value: '',
    operationType: 'aporte',
    notes: '',
  });

  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [editingIncomeId, setEditingIncomeId] = useState(null);
  const [incomeForm, setIncomeForm] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    investmentName: '',
    assetId: '',
    type: 'dividendos',
    value: '',
  });

  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [goalInput, setGoalInput] = useState('');

  const [movementsPage, setMovementsPage] = useState(0);
  const [movementsRowsPerPage, setMovementsRowsPerPage] = useState(10);

  useEffect(() => {
    if (!currentUser?.uid) return;
    getDoc(doc(db, 'users', currentUser.uid)).then((snap) => {
      const p = snap.exists() && snap.data().prosperity ? snap.data().prosperity : {};
      const inv = p.investments || {};
      setData({
        assets: Array.isArray(inv.assets) ? inv.assets : [],
        contributions: Array.isArray(inv.contributions)
          ? inv.contributions.map((c) => ({
              ...c,
              id: c.id || generateId(),
              operationType: c.operationType || 'aporte',
            }))
          : [],
        income: Array.isArray(inv.income)
          ? inv.income.map((i) => (i.id ? i : { ...i, id: generateId() }))
          : [],
        contributionGoal: Number(inv.contributionGoal) || 0,
        upcomingDividends: Array.isArray(inv.upcomingDividends) ? inv.upcomingDividends : [],
      });
    });
  }, [currentUser?.uid]);

  const saveInvestments = async (updates) => {
    if (!currentUser?.uid) return;
    try {
      const snap = await getDoc(doc(db, 'users', currentUser.uid));
      const current = snap.exists() ? snap.data() : {};
      const currentProsperity = current.prosperity || {};
      const currentInv = currentProsperity.investments || {};
      const merged = { ...currentInv, ...updates };
      await updateDoc(doc(db, 'users', currentUser.uid), {
        prosperity: { ...currentProsperity, investments: merged },
      });
      setData((prev) => ({ ...prev, ...updates }));
      setSnackbar({ open: true, message: 'Salvo com sucesso!', severity: 'success' });
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Erro ao salvar.', severity: 'error' });
    }
  };

  const now = new Date();
  const startMonth = startOfMonth(now);
  const endMonth = endOfMonth(now);

  const getSignedContributionValue = (c) => {
    const v = Number(c.value) || 0;
    return (c.operationType === 'retirada' ? -1 : 1) * v;
  };

  const contributionsByAsset = useMemo(() => {
    const map = {};
    data.contributions.forEach((c) => {
      if (!c.assetId) return;
      const id = c.assetId;
      if (!map[id]) {
        map[id] = { in: 0, out: 0 };
      }
      const value = Number(c.value) || 0;
      if (c.operationType === 'retirada') {
        map[id].out += value;
      } else {
        map[id].in += value;
      }
    });
    return map;
  }, [data.contributions]);

  const netContributionsByAsset = useMemo(() => {
    const map = {};
    data.contributions.forEach((c) => {
      if (!c.assetId) return;
      const signed = getSignedContributionValue(c);
      map[c.assetId] = (map[c.assetId] || 0) + signed;
    });
    return map;
  }, [data.contributions]);

  const totalInvested = useMemo(
    () =>
      data.assets.reduce((s, a) => {
        const base = Number(a.investedValue) || 0;
        const contrib = netContributionsByAsset[a.id] || 0;
        return s + base + contrib;
      }, 0),
    [data.assets, netContributionsByAsset]
  );

  const totalCurrent = useMemo(
    () =>
      data.assets.reduce((s, a) => {
        const base = Number(a.investedValue) || 0;
        const contrib = netContributionsByAsset[a.id] || 0;
        const cur = Number(a.currentValue) || base + contrib;
        return s + cur;
      }, 0),
    [data.assets, netContributionsByAsset]
  );
  const totalReturnPct = 0;

  const patrimonioInvestido = useMemo(
    () => data.contributions.reduce((s, c) => s + getSignedContributionValue(c), 0),
    [data.contributions]
  );

  const contributionsThisMonth = useMemo(
    () =>
      data.contributions.filter((c) => {
        const d = c.date ? parseISO(c.date + 'T12:00:00') : null;
        return d && isWithinInterval(d, { start: startMonth, end: endMonth });
      }),
    [data.contributions, startMonth, endMonth]
  );

  const aportesNoMes = contributionsThisMonth.reduce(
    (s, c) => s + getSignedContributionValue(c),
    0
  );

  const incomeThisMonth = useMemo(() => {
    return data.income.filter((i) => {
      const d = i.date ? parseISO(i.date + 'T12:00:00') : null;
      return d && isWithinInterval(d, { start: startMonth, end: endMonth });
    });
  }, [data.income, startMonth, endMonth]);
  const dividendosNoMes = incomeThisMonth.reduce((s, i) => s + (Number(i.value) || 0), 0);

  const rentabilidadeNoMesPct = patrimonioInvestido > 0 ? (dividendosNoMes / patrimonioInvestido) * 100 : 0;

  const pieData = useMemo(() => {
    const byType = {};
    data.assets.forEach((a) => {
      const net = (netContributionsByAsset[a.id] || 0) + (Number(a.investedValue) || 0);
      if (net <= 0) return;
      const t = a.type || 'acao';
      byType[t] = (byType[t] || 0) + net;
    });
    return Object.entries(byType)
      .filter(([, v]) => v > 0)
      .map(([type, value]) => ({
        name: ASSET_TYPES.find((x) => x.id === type)?.label || type,
        value,
        type,
      }));
  }, [data.assets, netContributionsByAsset]);

  const totalPieValue = useMemo(
    () => pieData.reduce((sum, item) => sum + (Number(item.value) || 0), 0),
    [pieData]
  );

  const inferRiskClass = (assetType, fallback = 'renda_variavel') => {
    switch (assetType) {
      case 'rendaFixa':
      case 'cdb':
      case 'tesouro_direto':
      case 'lci':
      case 'lca':
      case 'poupanca':
      case 'conta_remunerada':
        return 'renda_fixa';
      default:
        return fallback;
    }
  };

  const riskPieData = useMemo(() => {
    let rendaFixa = 0;
    let rendaVariavel = 0;

    data.assets.forEach((a) => {
      const net = (netContributionsByAsset[a.id] || 0) + (Number(a.investedValue) || 0);
      if (net <= 0) return;
      const risk = a.assetRisk || inferRiskClass(a.type || 'acao');
      if (risk === 'renda_fixa') {
        rendaFixa += net;
      } else {
        rendaVariavel += net;
      }
    });

    const result = [];
    if (rendaFixa > 0) {
      result.push({ name: 'Renda Fixa', value: rendaFixa, key: 'renda_fixa' });
    }
    if (rendaVariavel > 0) {
      result.push({ name: 'Renda Variável', value: rendaVariavel, key: 'renda_variavel' });
    }
    return result;
  }, [data.assets, netContributionsByAsset]);

  const totalRiskPieValue = useMemo(
    () => riskPieData.reduce((sum, item) => sum + (Number(item.value) || 0), 0),
    [riskPieData]
  );

  const incomeByMonth = useMemo(() => {
    const byMonth = {};
    data.income.forEach((i) => {
      if (!i.date) return;
      const key = i.date.slice(0, 7);
      byMonth[key] = (byMonth[key] || 0) + (Number(i.value) || 0);
    });
    const now = new Date();
    const result = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
      const key = format(d, 'yyyy-MM');
      result.push({
        month: format(d, 'MMM', { locale: ptBR }),
        valor: byMonth[key] || 0,
      });
    }
    return result;
  }, [data.income]);

  const avgAporteMensal = useMemo(() => {
    const byMonth = {};
    data.contributions.forEach((c) => {
      if (!c.date) return;
      const key = c.date.slice(0, 7);
      byMonth[key] = (byMonth[key] || 0) + getSignedContributionValue(c);
    });
    const vals = Object.values(byMonth);
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  }, [data.contributions]);

  const contributionGoal = Number(data.contributionGoal) || 0;
  const goalProgress = contributionGoal > 0 ? Math.min(100, (aportesNoMes / contributionGoal) * 100) : 0;

  const activeAssets = useMemo(
    () => data.assets.filter((a) => contributionsByAsset[a.id]),
    [data.assets, contributionsByAsset]
  );

  const sortedContributions = useMemo(
    () =>
      [...data.contributions].sort(
        (a, b) => new Date(a.date || 0) - new Date(b.date || 0)
      ),
    [data.contributions]
  );

  const movementsTotalPages = Math.max(
    1,
    Math.ceil(
      (sortedContributions.length || 1) / (movementsRowsPerPage || 10)
    )
  );

  const clampedMovementsPage =
    movementsPage >= movementsTotalPages ? movementsTotalPages - 1 : movementsPage;

  const pagedContributions = useMemo(() => {
    const start = clampedMovementsPage * movementsRowsPerPage;
    const end = start + movementsRowsPerPage;
    return sortedContributions.slice(start, end);
  }, [sortedContributions, clampedMovementsPage, movementsRowsPerPage]);

  const handleSaveAsset = () => {
    const name = (assetForm.name || '').trim();
    if (!name) {
      setSnackbar({ open: true, message: 'Informe o nome do ativo.', severity: 'warning' });
      return;
    }
    const investedValue = parseFloat(String(assetForm.investedValue).replace(',', '.')) || 0;
    if (editingAssetId) {
      const updated = data.assets.map((a) =>
        a.id === editingAssetId
          ? {
              ...a,
              name,
              type: assetForm.type,
              investedValue,
              date: assetForm.date,
            }
          : a
      );
      saveInvestments({ assets: updated });
      setEditingAssetId(null);
    } else {
      saveInvestments({
        assets: [...data.assets, { id: generateId(), name, type: assetForm.type, investedValue, date: assetForm.date }],
      });
    }
    setAssetDialogOpen(false);
    setAssetForm({ name: '', type: 'acao', investedValue: '', date: format(new Date(), 'yyyy-MM-dd') });
  };

  const handleEditAsset = (asset) => {
    setAssetForm({
      name: asset.name,
      type: asset.type || 'acao',
      investedValue: String(asset.investedValue ?? ''),
      date: (asset.date || '').slice(0, 10) || format(new Date(), 'yyyy-MM-dd'),
    });
    setEditingAssetId(asset.id);
    setAssetDialogOpen(true);
  };

  const handleDeleteAsset = (id) => {
    if (!window.confirm('Excluir este investimento?')) return;
    const updated = data.assets.filter((a) => a.id !== id);
    saveInvestments({ assets: updated });
  };

  const handleSaveContribution = () => {
    const value = parseFloat(String(contributionForm.value).replace(',', '.'));
    if (isNaN(value) || value <= 0) {
      setSnackbar({ open: true, message: 'Informe um valor válido.', severity: 'warning' });
      return;
    }
    const name = (contributionForm.investmentName || '').trim();
    if (!name) {
      setSnackbar({ open: true, message: 'Informe o nome do investimento.', severity: 'warning' });
      return;
    }
    // Garante que existe um ativo correspondente ao nome digitado
    let updatedAssets = data.assets;
    let assetId = null;
    const existingAsset = updatedAssets.find(
      (a) => a.name.toLowerCase() === name.toLowerCase()
    );
    if (existingAsset) {
      assetId = existingAsset.id;
    } else {
      const newAsset = {
        id: generateId(),
        name,
        type: contributionForm.assetType || 'acao',
        investedValue: 0,
        date: contributionForm.date,
        assetRisk: contributionForm.assetRisk || 'renda_fixa',
      };
      updatedAssets = [...updatedAssets, newAsset];
      assetId = newAsset.id;
    }

    let nextContributions;
    if (editingContributionId) {
      nextContributions = data.contributions.map((c) =>
        c.id === editingContributionId
          ? {
              ...c,
              date: contributionForm.date,
              assetId,
              assetType: contributionForm.assetType || '',
              assetRisk: contributionForm.assetRisk || 'renda_fixa',
              value,
              operationType: contributionForm.operationType || 'aporte',
              notes: (contributionForm.notes || '').trim(),
            }
          : c
      );
      setEditingContributionId(null);
    } else {
      nextContributions = [
        ...data.contributions,
        {
          id: generateId(),
          date: contributionForm.date,
          assetId,
          assetType: contributionForm.assetType || '',
          assetRisk: contributionForm.assetRisk || 'renda_fixa',
          value,
          operationType: contributionForm.operationType || 'aporte',
          notes: (contributionForm.notes || '').trim(),
        },
      ];
    }
    saveInvestments({ assets: updatedAssets, contributions: nextContributions });
    setContributionDialogOpen(false);
    setContributionForm({
      date: format(new Date(), 'yyyy-MM-dd'),
      investmentName: '',
      assetId: '',
      assetType: '',
      value: '',
      operationType: 'aporte',
      notes: '',
    });
  };

  const handleOpenContributionDialog = () => {
    setEditingContributionId(null);
    setContributionForm({
      date: format(new Date(), 'yyyy-MM-dd'),
      investmentName: '',
      assetId: '',
      assetType: '',
      assetRisk: 'renda_fixa',
      value: '',
      operationType: 'aporte',
      notes: '',
    });
    setContributionDialogOpen(true);
  };

  const handleEditContribution = (c) => {
    setEditingContributionId(c.id);
    setContributionForm({
      date: (c.date || '').slice(0, 10) || format(new Date(), 'yyyy-MM-dd'),
      investmentName: getAssetName(c.assetId) || '',
      assetId: c.assetId || '',
      value: String(c.value ?? ''),
      operationType: c.operationType || 'aporte',
      assetType: c.assetType || '',
      assetRisk: c.assetRisk || 'renda_fixa',
      notes: c.notes || '',
    });
    setContributionDialogOpen(true);
  };

  const handleDeleteContribution = (id) => {
    if (!window.confirm('Excluir esta movimentação?')) return;
    const updated = data.contributions.filter((c) => c.id !== id);
    const usedIds = new Set(
      updated
        .filter((c) => c.assetId)
        .map((c) => c.assetId)
    );
    const filteredAssets = data.assets.filter((a) => usedIds.has(a.id));
    saveInvestments({ contributions: updated, assets: filteredAssets });
  };

  const handleSaveIncome = () => {
    const value = parseFloat(String(incomeForm.value).replace(',', '.'));
    if (isNaN(value) || value <= 0) {
      setSnackbar({ open: true, message: 'Informe um valor válido.', severity: 'warning' });
      return;
    }
    const name = (incomeForm.investmentName || '').trim();
    let updatedAssets = data.assets;
    let assetId = incomeForm.assetId || null;
    if (name) {
      const existingAsset = updatedAssets.find(
        (a) => a.name.toLowerCase() === name.toLowerCase()
      );
      if (existingAsset) {
        assetId = existingAsset.id;
      }
    }
    if (editingIncomeId) {
      const updated = data.income.map((i) =>
        i.id === editingIncomeId
          ? {
              ...i,
              date: incomeForm.date,
              assetId,
              type: incomeForm.type,
              value,
            }
          : i
      );
      saveInvestments({ assets: updatedAssets, income: updated });
      setEditingIncomeId(null);
    } else {
      const nextIncome = [
        ...data.income,
        {
          id: generateId(),
          date: incomeForm.date,
          assetId,
          type: incomeForm.type,
          value,
        },
      ];
      saveInvestments({
        assets: updatedAssets,
        income: nextIncome,
      });
    }
    setIncomeDialogOpen(false);
    setIncomeForm({
      date: format(new Date(), 'yyyy-MM-dd'),
      investmentName: '',
      assetId: '',
      type: 'dividendos',
      value: '',
    });
  };

  const handleOpenIncomeDialog = () => {
    setEditingIncomeId(null);
    setIncomeForm({
      date: format(new Date(), 'yyyy-MM-dd'),
      investmentName: '',
      assetId: '',
      type: 'dividendos',
      value: '',
    });
    setIncomeDialogOpen(true);
  };

  const handleEditIncome = (i) => {
    setEditingIncomeId(i.id);
    setIncomeForm({
      date: (i.date || '').slice(0, 10) || format(new Date(), 'yyyy-MM-dd'),
      investmentName: getAssetName(i.assetId) || '',
      assetId: i.assetId || '',
      type: i.type || 'dividendos',
      value: String(i.value ?? ''),
    });
    setIncomeDialogOpen(true);
  };

  const handleDeleteIncome = (id) => {
    if (!window.confirm('Excluir este rendimento?')) return;
    const updated = data.income.filter((i) => i.id !== id);
    saveInvestments({ income: updated });
  };

  const handleSaveGoal = () => {
    const goal = parseFloat(String(goalInput).replace(',', '.'));
    saveInvestments({ contributionGoal: isNaN(goal) || goal < 0 ? 0 : goal });
    setGoalDialogOpen(false);
    setGoalInput('');
  };

  const getAssetName = (assetId) => data.assets.find((a) => a.id === assetId)?.name || '-';

  return (
    <PageLayout title="Investimentos" hideAddButton customColors={customColors}>
      <Box sx={{ maxWidth: 1000, mx: 'auto', pb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TrendingUpIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
          <Box>
            <Typography variant="h6" color="primary">
              Acompanhamento e motivação
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Acompanhe seu patrimônio, aportes e renda passiva sem complexidade.
            </Typography>
          </Box>
        </Box>

        {/* CARDS RESUMO */}
        <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600, mb: 1.5 }}>
          Resumo
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">💰 Patrimônio Investido</Typography>
                <Typography variant="subtitle1" fontWeight={600}>{formatCurrency(patrimonioInvestido)}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">Soma dos aportes</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">📊 Rentabilidade no mês</Typography>
                <Typography variant="subtitle1" fontWeight={600} color="success.main">{rentabilidadeNoMesPct.toFixed(1)}%</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">📈 Rentabilidade total</Typography>
                <Typography variant="subtitle1" fontWeight={600} color="success.main">{totalReturnPct.toFixed(1)}%</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">💵 Dividendos no mês</Typography>
                <Typography variant="subtitle1" fontWeight={600}>{formatCurrency(dividendosNoMes)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">🏦 Aportes no mês</Typography>
                <Typography variant="subtitle1" fontWeight={600}>{formatCurrency(aportesNoMes)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">📅 Próximos dividendos</Typography>
                <Typography variant="body2" color="text.secondary">Registre em Rendimentos</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* META DE APORTE */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>
                Meta de Aporte
              </Typography>
              <Button size="small" variant="outlined" onClick={() => { setGoalInput(String(contributionGoal)); setGoalDialogOpen(true); }}>
                {contributionGoal > 0 ? 'Alterar meta' : 'Definir meta'}
              </Button>
            </Box>
            {contributionGoal > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Meta: {formatCurrency(contributionGoal)} | Já aportou: {formatCurrency(aportesNoMes)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={goalProgress}
                  color={goalProgress >= 100 ? 'success' : 'primary'}
                  sx={{ height: 10, borderRadius: 1, mt: 1 }}
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Defina uma meta mensal de aporte para acompanhar sua disciplina.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* GRÁFICOS */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                  Patrimônio investido
                </Typography>
                <Typography variant="h5" fontWeight={700}>{formatCurrency(patrimonioInvestido)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Soma de todos os aportes (aportes menos retiradas) registrados.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                  Distribuição da carteira por tipo
                </Typography>
                {pieData.length > 0 ? (
                  <Box sx={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="name"
                          labelLine={false}
                          label={({
                            cx, cy, midAngle, innerRadius, outerRadius, percent, value, name,
                          }) => {
                            const radius = innerRadius + (outerRadius - innerRadius) * 0.7;
                            const RADIAN = Math.PI / 180;
                            const x = cx + radius * Math.cos(-midAngle * RADIAN);
                            const y = cy + radius * Math.sin(-midAngle * RADIAN);
                            const pct = totalPieValue > 0
                              ? ((Number(value) || 0) / totalPieValue) * 100
                              : 0;
                            if (pct < 5) return null;
                            return (
                              <text
                                x={x}
                                y={y}
                                fill="#fff"
                                textAnchor="middle"
                                dominantBaseline="central"
                                style={{ fontSize: 11 }}
                              >
                                {`${name}: ${pct.toFixed(1)}%`}
                              </text>
                            );
                          }}
                        >
                          {pieData.map((entry, i) => (
                            <Cell key={i} fill={PIE_COLORS[entry.type] || '#888'} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(v, _name, props) => {
                            const value = Number(v) || 0;
                            const pct =
                              totalPieValue > 0
                                ? (value / totalPieValue) * 100
                                : 0;
                            return [
                              `${pct.toFixed(1)}% - ${formatCurrency(value)}`,
                              props?.payload?.name || '',
                            ];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Adicione investimentos e movimentações para ver a distribuição.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                  Renda fixa x renda variável
                </Typography>
                {riskPieData.length > 0 ? (
                  <Box sx={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={riskPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="name"
                          labelLine={false}
                          label={({
                            cx, cy, midAngle, innerRadius, outerRadius, value, name,
                          }) => {
                            const radius = innerRadius + (outerRadius - innerRadius) * 0.7;
                            const RADIAN = Math.PI / 180;
                            const x = cx + radius * Math.cos(-midAngle * RADIAN);
                            const y = cy + radius * Math.sin(-midAngle * RADIAN);
                            const pct = totalRiskPieValue > 0
                              ? ((Number(value) || 0) / totalRiskPieValue) * 100
                              : 0;
                            if (pct < 5) return null;
                            return (
                              <text
                                x={x}
                                y={y}
                                fill="#fff"
                                textAnchor="middle"
                                dominantBaseline="central"
                                style={{ fontSize: 11 }}
                              >
                                {`${name}: ${pct.toFixed(1)}%`}
                              </text>
                            );
                          }}
                        >
                          {riskPieData.map((entry, i) => (
                            <Cell
                              key={i}
                              fill={entry.key === 'renda_fixa' ? '#2E7D32' : '#1565C0'}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(v, _name, props) => {
                            const value = Number(v) || 0;
                            const pct =
                              totalRiskPieValue > 0
                                ? (value / totalRiskPieValue) * 100
                                : 0;
                            return [
                              `${pct.toFixed(1)}% - ${formatCurrency(value)}`,
                              props?.payload?.name || '',
                            ];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Registre investimentos para ver a proporção entre renda fixa e variável.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* INVESTIMENTOS E MOVIMENTAÇÕES */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>
                Investimentos e movimentações
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenContributionDialog}
              >
                Registrar movimentação
              </Button>
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Meus investimentos
            </Typography>
            {activeAssets.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Nenhum ativo cadastrado ainda.
              </Typography>
            ) : (
              <TableContainer sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Ativo</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>RF/RV</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Valor Investido</TableCell>
                      <TableCell>Valor Retirado</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeAssets.map((a) => {
                      const sums = contributionsByAsset[a.id] || { in: 0, out: 0 };
                      const inv = sums.in || 0;
                      const tipo =
                        ASSET_TYPES.find((t) => t.id === a.type)?.label || a.type || '-';
                      const riskLabel =
                        a.assetRisk === 'renda_fixa'
                          ? 'Renda Fixa'
                          : a.assetRisk === 'renda_variavel'
                          ? 'Renda Variável'
                          : inferRiskClass(a.type || 'acao') === 'renda_fixa'
                          ? 'Renda Fixa'
                          : 'Renda Variável';
                      return (
                        <TableRow key={a.id} hover>
                          <TableCell>{a.name}</TableCell>
                          <TableCell>{tipo}</TableCell>
                          <TableCell>{riskLabel}</TableCell>
                          <TableCell>
                            {a.date
                              ? format(
                                  parseISO((a.date || '').slice(0, 10) + 'T12:00:00'),
                                  'dd/MM/yyyy',
                                  { locale: ptBR }
                                )
                              : '-'}
                          </TableCell>
                          <TableCell>{formatCurrency(inv)}</TableCell>
                          <TableCell>{formatCurrency(sums.out || 0)}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleEditAsset(a)} aria-label="Editar">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteAsset(a.id)} aria-label="Excluir">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Movimentações (aportes e retiradas)
              </Typography>
            </Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">📅 Aportes no mês</Typography>
                  <Typography variant="h6">{formatCurrency(aportesNoMes)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">📊 Média de aporte mensal</Typography>
                  <Typography variant="h6">{formatCurrency(avgAporteMensal)}</Typography>
                </Paper>
              </Grid>
            </Grid>
            {data.contributions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhuma movimentação registrada.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Ativo</TableCell>
                      <TableCell>Valor</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedContributions.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell>
                          {c.date
                            ? format(
                                parseISO(c.date + 'T12:00:00'),
                                'dd/MM/yy',
                                { locale: ptBR }
                              )
                            : '-'}
                        </TableCell>
                        <TableCell>{c.operationType === 'retirada' ? 'Retirada' : 'Aporte'}</TableCell>
                        <TableCell>{c.assetId ? getAssetName(c.assetId) : '-'}</TableCell>
                        <TableCell>{formatCurrency(getSignedContributionValue(c))}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleEditContribution(c)} aria-label="Editar">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteContribution(c.id)} aria-label="Excluir">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {data.contributions.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 1.5,
                  flexWrap: 'wrap',
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Linhas por página:
                  </Typography>
                  <FormControl size="small">
                    <Select
                      value={movementsRowsPerPage}
                      onChange={(e) => {
                        const value = Number(e.target.value) || 10;
                        setMovementsRowsPerPage(value);
                        setMovementsPage(0);
                      }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Página {clampedMovementsPage + 1} de {movementsTotalPages}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={clampedMovementsPage === 0}
                      onClick={() =>
                        setMovementsPage((prev) => (prev > 0 ? prev - 1 : 0))
                      }
                    >
                      Anterior
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={clampedMovementsPage >= movementsTotalPages - 1}
                      onClick={() =>
                        setMovementsPage((prev) =>
                          prev < movementsTotalPages - 1 ? prev + 1 : prev
                        )
                      }
                    >
                      Próxima
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* RENDIMENTOS */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>
                Rendimentos
              </Typography>
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleOpenIncomeDialog}>
                Registrar rendimento
              </Button>
            </Box>
            {incomeByMonth.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>📊 Renda passiva por mês</Typography>
                <Box sx={{ height: 200, mb: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={incomeByMonth}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))} />
                      <Tooltip formatter={(v) => [formatCurrency(v), '']} />
                      <Bar dataKey="valor" name="Renda passiva" fill="#2E7D32" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </>
            )}
            {data.income.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum rendimento registrado. Registre dividendos, JCP, juros etc.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Ativo</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Valor</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...data.income]
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .slice(0, 15)
                      .map((i) => (
                        <TableRow key={i.id} hover>
                          <TableCell>
                            {i.date
                              ? format(
                                  parseISO(i.date + 'T12:00:00'),
                                  'dd/MM/yy',
                                  { locale: ptBR }
                                )
                              : '-'}
                          </TableCell>
                          <TableCell>{i.assetId ? getAssetName(i.assetId) : '-'}</TableCell>
                          <TableCell>{INCOME_TYPES.find((t) => t.id === i.type)?.label || i.type}</TableCell>
                          <TableCell>{formatCurrency(i.value)}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleEditIncome(i)} aria-label="Editar">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteIncome(i.id)} aria-label="Excluir">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* DIALOGS */}
        <Dialog open={assetDialogOpen} onClose={() => setAssetDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingAssetId ? 'Editar ativo' : 'Adicionar investimento'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                size="small"
                label="Nome do ativo"
                value={assetForm.name}
                onChange={(e) => setAssetForm((f) => ({ ...f, name: e.target.value }))}
                fullWidth
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={assetForm.type}
                  label="Tipo"
                  onChange={(e) => setAssetForm((f) => ({ ...f, type: e.target.value }))}
                >
                  {ASSET_TYPES.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Valor investido"
                type="text"
                value={assetForm.investedValue}
                onChange={(e) => setAssetForm((f) => ({ ...f, investedValue: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                fullWidth
              />
              <TextField
                size="small"
                label="Data"
                type="date"
                value={assetForm.date}
                onChange={(e) => setAssetForm((f) => ({ ...f, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssetDialogOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSaveAsset}>Salvar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={contributionDialogOpen} onClose={() => { setContributionDialogOpen(false); setEditingContributionId(null); }} maxWidth="xs" fullWidth>
          <DialogTitle>{editingContributionId ? 'Editar movimentação' : 'Registrar aporte/retirada'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                size="small"
                label="Data"
                type="date"
                value={contributionForm.date}
                onChange={(e) => setContributionForm((f) => ({ ...f, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={contributionForm.operationType}
                  label="Tipo"
                  onChange={(e) => setContributionForm((f) => ({ ...f, operationType: e.target.value }))}
                >
                  <MenuItem value="aporte">Aporte</MenuItem>
                  <MenuItem value="retirada">Retirada</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Nome do investimento"
                type="text"
                value={contributionForm.investmentName}
                onChange={(e) => setContributionForm((f) => ({ ...f, investmentName: e.target.value }))}
                fullWidth
                list="investment-name-options"
                helperText="Digite o nome ou selecione um já utilizado"
              />
              <datalist id="investment-name-options">
                {Array.from(
                  new Set([
                    ...data.assets.map((a) => a.name),
                    ...data.contributions.map((c) => getAssetName(c.assetId)).filter(Boolean),
                  ])
                ).map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
              <FormControl size="small" fullWidth>
                <InputLabel>Tipo do investimento</InputLabel>
                <Select
                  value={contributionForm.assetType}
                  label="Tipo do investimento"
                  onChange={(e) => setContributionForm((f) => ({ ...f, assetType: e.target.value }))}
                >
                  <MenuItem value="cdb">CDB</MenuItem>
                  <MenuItem value="tesouro_direto">Tesouro Direto</MenuItem>
                  <MenuItem value="lci">LCI</MenuItem>
                  <MenuItem value="lca">LCA</MenuItem>
                  <MenuItem value="fii">FII</MenuItem>
                  <MenuItem value="acoes">Ações</MenuItem>
                  <MenuItem value="etf">ETF</MenuItem>
                  <MenuItem value="cripto">Criptomoedas</MenuItem>
                  <MenuItem value="fundos">Fundos de Investimento</MenuItem>
                  <MenuItem value="poupanca">Poupança</MenuItem>
                  <MenuItem value="conta_remunerada">Conta remunerada</MenuItem>
                  <MenuItem value="outro">Outro</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>RF/RV</InputLabel>
                <Select
                  value={contributionForm.assetRisk}
                  label="RF/RV"
                  onChange={(e) => setContributionForm((f) => ({ ...f, assetRisk: e.target.value }))}
                >
                  <MenuItem value="renda_fixa">Renda Fixa</MenuItem>
                  <MenuItem value="renda_variavel">Renda Variável</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Valor"
                type="text"
                value={contributionForm.value}
                onChange={(e) => setContributionForm((f) => ({ ...f, value: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                fullWidth
              />
              <TextField
                size="small"
                label="Observações"
                type="text"
                value={contributionForm.notes}
                onChange={(e) => setContributionForm((f) => ({ ...f, notes: e.target.value }))}
                multiline
                minRows={2}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContributionDialogOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSaveContribution}>Salvar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={incomeDialogOpen} onClose={() => { setIncomeDialogOpen(false); setEditingIncomeId(null); }} maxWidth="xs" fullWidth>
          <DialogTitle>{editingIncomeId ? 'Editar rendimento' : 'Registrar rendimento'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                size="small"
                label="Data"
                type="date"
                value={incomeForm.date}
                onChange={(e) => setIncomeForm((f) => ({ ...f, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                size="small"
                label="Ativo"
                type="text"
                value={incomeForm.investmentName}
                onChange={(e) => setIncomeForm((f) => ({ ...f, investmentName: e.target.value }))}
                fullWidth
                list="income-asset-options"
                helperText="Digite o ativo ou selecione um já utilizado"
              />
              <datalist id="income-asset-options">
                {Array.from(
                  new Set([
                    ...data.assets.map((a) => a.name),
                    ...data.income
                      .map((i) => getAssetName(i.assetId))
                      .filter((n) => n && n !== '-'),
                  ])
                ).map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
              <FormControl size="small" fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={incomeForm.type}
                  label="Tipo"
                  onChange={(e) => setIncomeForm((f) => ({ ...f, type: e.target.value }))}
                >
                  {INCOME_TYPES.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Valor"
                type="text"
                value={incomeForm.value}
                onChange={(e) => setIncomeForm((f) => ({ ...f, value: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIncomeDialogOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSaveIncome}>Salvar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={goalDialogOpen} onClose={() => setGoalDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Meta de aporte mensal</DialogTitle>
          <DialogContent>
            <TextField
              size="small"
              label="Meta (R$)"
              type="text"
              value={goalInput}
              onChange={(e) => setGoalInput(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              fullWidth
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGoalDialogOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSaveGoal}>Salvar</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </PageLayout>
  );
}
